import { useEffect, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Config from "../../Config";
import { IncomeExpenseSummarySkeleton } from "../../Components/SkeletonLoading";
import { BoxContainer, BoxHead, BoxTitle, Underline } from "../../Components/Styles/PageStyles";
import { IoChevronBackOutline } from "react-icons/io5";
import tw from "tailwind-styled-components";

const DailySummary = () => {
    const user = useSelector((state) => state.UserReducer.user);
    const { p: propId } = useParams();
    const { search } = useLocation();
    const navigate = useNavigate();
    const query = new URLSearchParams(search);
    const month = query.get("m");
    const year = query.get("y");

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchDaily = async () => {
        let url = `client/pendingrent/month/daily?m=${month}&y=${year}`;
        if (propId !== '0')
        {
            url = `property/pendingrent/month/daily/${propId}?m=${month}&y=${year}`;
        }
        setLoading(true);
        const res = await axios.get(
            `${Config.apiUrl}/${url}`,
            {
                headers: {
                    authorization: "Bearer " + user.token,
                },
            }
        );
        setLoading(false);

        if (res.data.isSuccess) {
            setData(res.data.data?.data || []);
        }
    };

    useEffect(() => {
        fetchDaily();
    }, [month, year]);

    const handleBackClick = () => {
        navigate("/income-expense");
    };
    return (
        <BoxContainer>
            <BoxHead>
                <BoxTitle className="!mb-6 whitespace-nowrap">
                    <BackWrapper>
                        <BackButton onClick={handleBackClick}>
                            <IoChevronBackOutline size={20} />
                        </BackButton>
                        <div >
                            <span className="flex items-center">
                                Daily Summary – {month}-{year}
                            </span>
                            <Underline />
                        </div>
                    </BackWrapper>
                </BoxTitle>
            </BoxHead>
            {loading && <IncomeExpenseSummarySkeleton />}

            {!loading && data.length === 0 && (
                <p className="text-gray-500 text-sm">No records found.</p>
            )}

            {!loading && data.length > 0 && (
                <div className="grid gap-6">
                    {data.map((item, idx) => (
                        <div
                            key={idx}
                            className="rounded-2xl border border-gray-200 overflow-hidden"
                        >
                            <div className="bg-gray-200 text-gray-800 p-4 flex sm:flex-row flex-col sm:justify-between sm:items-center gap-4">
                                <h2 className="font-semibold">
                                    {item.date}
                                </h2>
                                <span
                                    className={`px-3 py-1 rounded-full text-xs sm:text-sm font-semibold ${item.netProfitLoss >= 0
                                        ? "bg-green-100 text-green-700"
                                        : "bg-red-100 text-red-700"
                                        }`}
                                >
                                    {item.netProfitLoss >= 0 ? "Profit" : "Loss"} ₹
                                    {Math.abs(item.netProfitLoss).toLocaleString()}
                                </span>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-5">
                                <div className="space-y-4">
                                    <SectionTitle title="Income Overview" color="green-500" />
                                    <div className="grid grid-cols-2 gap-3 text-base">
                                        <Stat label="Total Income" value={item.totalIncome} color="text-green-700" />
                                        <Stat label="Rent Received" value={item.totalRent} />
                                        <Stat label="Online" value={item.totalOnline} />
                                        <Stat label="Kipinn App" value={item.totalPayu} />
                                        <Stat label="Cash" value={item.totalOffline} />
                                        <Stat label="Security" value={item.totalSecurity} />
                                        <Stat label="Other Income" value={item.otherIncome} />
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <SectionTitle title="Expense Overview" color="red-500" />
                                    <div className="grid grid-cols-2 gap-3 text-sm">
                                        <Stat label="Total Expense" value={item.totalExpense} color="text-red-700" />
                                        <Stat label="Pending Dues" value={item.totalDues} />
                                        <Stat label="Refund" value={item.totalRefund} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

        </BoxContainer >
    );
};
const SectionTitle = ({ title, color }) => (
    <h3
        className={`text-sm sm:text-base font-semibold text-${color} border-l-4 border-${color} pl-2`}
    >
        {title}
    </h3>
);
const Stat = ({ label, value, color = "text-gray-800" }) => (
    <div className="flex flex-col items-center justify-center bg-white rounded-xl border border-gray-200 p-4 text-center">
        <span className="text-sm text-gray-500 flex items-center justify-center gap-1">
            {label}
        </span>
        <span className={`font-semibold text-xl ${color}`}>
            ₹{Number(value || 0).toLocaleString()}
        </span>
    </div>
);
const BackWrapper = tw.div`flex items-center`;
const BackButton = tw.button`hover:bg-gray-200 items-center -mt-1 p-1 rounded-md transition-colors`;
export default DailySummary;
